import { computed, Injectable, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslocoService } from '@jsverse/transloco';
import { formatMonthYear, localeForLang, monthNames } from 'app/core/utils/locale';

/** First year with data */
const FIRST_YEAR = 2025;

/**
 * Period of the dashboard, sent as is to the API:
 * 'all', a year ('2026') or a month ('2026-09').
 */
@Injectable({ providedIn: 'root' })
export class DashboardFiltersService {
	private readonly transloco = inject(TranslocoService);

	/** Translation of the active language; emits again each time a language file finishes loading */
	private readonly translation = toSignal(this.transloco.selectTranslation());

	private readonly locale = computed(() => {
		this.translation();
		return localeForLang(this.transloco.getActiveLang());
	});

	/** Short month names for the active language ('Ene', 'Feb'... in Spanish) */
	readonly monthLabels = computed(() => monthNames(this.locale(), 'short'));

	private readonly now = new Date();
	readonly currentYear = this.now.getFullYear();
	readonly currentMonth = this.now.getMonth(); // 0-11

	readonly years: number[] = Array.from({ length: this.currentYear - FIRST_YEAR + 1 }, (_, i) => FIRST_YEAR + i);

	/** Default: the current month */
	readonly selectedPeriod = signal<string>(this.monthKey(this.currentYear, this.currentMonth));

	readonly label = computed(() => {
		const period = this.selectedPeriod();
		this.translation(); // re-evaluate once the active language is loaded
		if (period === 'all') return this.transloco.translate('dashboard.period.all');
		const [year, month] = period.split('-');
		return month
			? formatMonthYear(Number(year), Number(month) - 1, this.locale())
			: this.transloco.translate('dashboard.period.year', { year });
	});

	setAll(): void {
		this.selectedPeriod.set('all');
	}

	setYear(year: number): void {
		this.selectedPeriod.set(String(year));
	}

	setMonth(year: number, month: number): void {
		this.selectedPeriod.set(this.monthKey(year, month));
	}

	private monthKey(year: number, month: number): string {
		return `${year}-${String(month + 1).padStart(2, '0')}`;
	}
}
